import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { getFormScreenForVirtualCard } from 'src/app/UpdateVirtualCardForm/getFormScreenForVirtualCard'
import { createSetReceiptAttachmentIds } from 'src/app/UpdateVirtualCardForm/redux/updateVirtualCardForm.actions'
import {
  selectErrorDetailMapFromState,
  selectPuttingVirtualCardCancelFromState,
  selectPuttingVirtualCardCancelUpdateFromState,
  selectPuttingVirtualCardFailureFromState,
  selectPuttingVirtualCardFromState,
  selectPuttingVirtualCardRejectFromState,
  selectReceiptAttachmentModelsList,
  selectTopLevelErrorFromState,
  selectUploadingAttachmentsFromState,
} from 'src/app/UpdateVirtualCardForm/redux/updateVirtualCardForm.selectors'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import UpdateVirtualCardGuestAccessContent from 'src/components/UpdateVirtualCardGuestAccessContent/UpdateVirtualCardGuestAccessContent'
import UpdateVirtualCardIssuerCycleContent from 'src/components/UpdateVirtualCardIssuerCycleContent/UpdateVirtualCardIssuerCycleContent'
import UpdateVirtualCardPickContent from 'src/components/UpdateVirtualCardPickContent/UpdateVirtualCardPickContent'
import UpdateVirtualCardRecurringContent from 'src/components/UpdateVirtualCardRecurringContent/UpdateVirtualCardRecurringContent'
import UpdateVirtualCardStandardContent from 'src/components/UpdateVirtualCardStandardContent/UpdateVirtualCardStandardContent'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ExtendCurrency, VirtualCardTypes } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useVirtualCardFormStatistics } from 'src/hooks/virtualCardForm'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  ToastType,
  VirtualCardSecureModel,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import {
  useVirtualCardFormCreditCards,
  type VirtualCardFormScreen,
} from 'src/queries/creditCards/domain/useVirtualCardFormCreditCards'
import {
  isCardApprovalRuleEnabled,
  orgCardApprovalRulesQueries,
} from 'src/queries/organizations/cardApprovalRules/cardApprovalRules.queries'
import {
  automationRulesQueryKeys,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { createSetIsRequestCompletedPopupOpen } from 'src/redux/actions/app.actions'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import {
  putVirtualCardAndOpenToastAndCloseFlyout,
  putVirtualCardCancelAndCloseModalOrRedirect,
  putVirtualCardCancelUpdateAndOpenToastAndCloseModal,
  putVirtualCardFinalApprovalAndOpenToastAndCloseFlyout,
  putVirtualCardRejectAndOpenToastAndCloseModal,
} from 'src/redux/actions/data/virtualCards.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandFeaturesFromState,
} from 'src/redux/selectors/session.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toDollars } from 'src/utils/currency.utils'

interface UpdateVirtualCardFormProps {
  virtualCardId: string
  virtualCard: VirtualCardSecureModel | undefined
  virtualCardPermissions: VirtualCardPermissionsModel | undefined
  recipient: UserListModel | undefined
  cardholder: UserListModel | undefined
  loadingFlyout: boolean
  screen?: UpdateVirtualCardScreen
}

const UpdateVirtualCardForm: React.FC<UpdateVirtualCardFormProps> = (props) => {
  const translate = useTranslate()

  const {
    cardholder,
    loadingFlyout,
    recipient,
    virtualCard,
    virtualCardId,
    virtualCardPermissions,
  } = props
  const queryClient = useQueryClient()
  const queryParams = useSelector(selectQueryParams)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const puttingVirtualCard = useSelector(selectPuttingVirtualCardFromState)
  const puttingVirtualCardCancel = useSelector(
    selectPuttingVirtualCardCancelFromState,
  )
  const puttingVirtualCardCancelUpdate = useSelector(
    selectPuttingVirtualCardCancelUpdateFromState,
  )
  const puttingVirtualCardReject = useSelector(
    selectPuttingVirtualCardRejectFromState,
  )
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const appName = useSelector(selectAppName)
  const showRequestCompletedPopup = useSelector(
    selectPuttingVirtualCardFailureFromState,
  )
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: cardApprovalRules } = useQuery(
    orgCardApprovalRulesQueries.detail(userOrganization?.id),
  )
  const cardApprovalsEnabled = isCardApprovalRuleEnabled(cardApprovalRules)
  const user = useCurrentUser()
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )

  const { analytics } = useAnalyticsContext()

  // Gate on virtualCard being loaded to avoid fetching with wrong screen type
  const newHooksEnabled = virtualCard !== undefined
  const formStats = useVirtualCardFormStatistics({
    variant: 'update',
    enabled: newHooksEnabled,
  })

  const updateScreenToFormScreen: Record<
    UpdateVirtualCardScreen,
    VirtualCardFormScreen
  > = {
    [UpdateVirtualCardScreen.Standard]: 'Standard',
    [UpdateVirtualCardScreen.BillPay]: 'BillPay',
    [UpdateVirtualCardScreen.Recurring]: 'Recurring',
    [UpdateVirtualCardScreen.GuestAccess]: 'GuestAccess',
    [UpdateVirtualCardScreen.RecurringGuest]: 'RecurringGuest',
    [UpdateVirtualCardScreen.DecliningBalance]: 'DecliningBalance',
    [UpdateVirtualCardScreen.IssuerCycle]: 'IssuerCycle',
    [UpdateVirtualCardScreen.Pick]: 'Standard',
  }

  const activeUpdateFormScreen: VirtualCardFormScreen =
    props.screen !== undefined
      ? updateScreenToFormScreen[props.screen]
      : getFormScreenForVirtualCard(virtualCard)

  const formCreditCards = useVirtualCardFormCreditCards(
    activeUpdateFormScreen,
    {
      enabled: newHooksEnabled,
    },
  )

  const formCreditCardStandardOptions = useMemo(
    () => formCreditCards.standard.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.standard.options],
  )
  const formCreditCardBillPayOptions = useMemo(
    () => formCreditCards.billPay.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.billPay.options],
  )
  const formCreditCardRecurringOptions = useMemo(
    () => formCreditCards.recurring.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.recurring.options],
  )
  const formCreditCardGuestAccessOptions = useMemo(
    () => formCreditCards.guestAccess.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.guestAccess.options],
  )
  const formCreditCardBalanceLockOptions = useMemo(
    () => formCreditCards.balanceLock.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.balanceLock.options],
  )
  const formCreditCardIssuerCycleOptions = useMemo(
    () => formCreditCards.issuerCycle.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.issuerCycle.options],
  )

  const canUpdateVirtualCard =
    virtualCardPermissions?.canUpdateVirtualCard || false
  const showPreApprovalSoftAlert =
    virtualCard?.isPending && virtualCard?.recipientId !== user?.id
      ? cardApprovalsEnabled && cardApprovalRules?.approverId !== user?.id
      : cardApprovalsEnabled &&
        cardApprovalRules?.approverId !== user?.id &&
        virtualCardPermissions?.canUpdateVirtualCard

  const dispatch = useFlyoutDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments()

  useEffect((): void => {
    if (typeof queryParams.creditCardId === 'string') {
      dispatch(getCreditCard(queryParams.creditCardId))
    }
  }, [queryParams['creditCardId']])

  useEffect((): void => {
    if (
      (virtualCardPermissions?.canUpdateVirtualCard &&
        virtualCard?.creditCardId !== undefined) ||
      (virtualCard?.creditCardId !== undefined &&
        virtualCard.awaitingApprovalStatus !== undefined)
    ) {
      dispatch(getCreditCard(virtualCard?.creditCardId))
    }
  }, [virtualCardPermissions, virtualCard?.creditCardId])

  useEffect((): void => {
    if (showRequestCompletedPopup) {
      dispatch(createSetIsRequestCompletedPopupOpen(true))
    }
  }, [showRequestCompletedPopup])

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }
  const dispatchPutVirtualCardAndOpenToastAndCloseFlyout = (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ): void => {
    // if the current user isnt the recipient or the card approver, and card approvals are enabled
    const finalApprovalToastBool =
      virtualCard?.recipient?.id !== user?.id &&
      cardApprovalsEnabled &&
      cardApprovalRules?.approverId !== user?.id
    // if the balance is greater than the threshold, and card approvals are enabled
    const showCardApproverToast =
      body.currency === ExtendCurrency.Cad
        ? body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.cadCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.cadCents
        : body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.usdCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.usdCents
    const toastType =
      finalApprovalToastBool && showCardApproverToast && virtualCard?.isPending
        ? ToastType.ApproveRequestOrCreate
        : finalApprovalToastBool && virtualCard?.isActive
          ? ToastType.ApproveUpdate
          : virtualCard?.cardType === VirtualCardType.BillPay
            ? ToastType.BillPayApprove
            : undefined
    // if card approvals are enabled and the current user is the card approver but not the funding source owner
    const virtualCardFinalApproval =
      cardApprovalsEnabled &&
      cardApprovalRules?.approverId === user?.id &&
      virtualCard?.awaitingApprovalStatus ===
        ApprovalStatus.AwaitingApproverRuleApproval

    const virtualCardResult = virtualCardFinalApproval
      ? dispatch(
          putVirtualCardFinalApprovalAndOpenToastAndCloseFlyout(
            id,
            { pendingRevisionId: body.pendingRevisionId ?? null },
            openToast,
            toastType,
            virtualCard?.billPayMeta?.vendorName,
          ),
        )
      : dispatch(
          putVirtualCardAndOpenToastAndCloseFlyout(
            id,
            body,
            openToast,
            toastType,
          ),
        )

    // TODO: React Query move into onSuccess of virtualCard queries when we refactor virtual cards
    virtualCardResult.then(() => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    })
  }
  const dispatchPutVirtualCardCancelAndCloseModalOrRedirect = (
    id: string,
    isPending: boolean,
  ): void => {
    dispatch(putVirtualCardCancelAndCloseModalOrRedirect(id, isPending))
  }
  const dispatchPutVirtualCardRejectAndCloseModal = (id: string): void => {
    dispatch(
      putVirtualCardRejectAndOpenToastAndCloseModal(
        id,
        virtualCard?.pending?.id,
        virtualCard?.cardType,
      ),
    )
  }
  const dispatchPutVirtualCardCancelUpdateAndCloseModal = (
    id: string,
  ): void => {
    dispatch(putVirtualCardCancelUpdateAndOpenToastAndCloseModal(id))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }
  const isOrganizationCardApproverMe =
    user?.id === cardApprovalRules?.approverId

  const currentScreen =
    props.screen ??
    (virtualCard !== undefined
      ? virtualCard.isIssuerCycle
        ? UpdateVirtualCardScreen.IssuerCycle
        : virtualCard.features.balanceLocked
          ? UpdateVirtualCardScreen.DecliningBalance
          : virtualCard.direct
            ? virtualCard.recurs
              ? UpdateVirtualCardScreen.RecurringGuest
              : UpdateVirtualCardScreen.GuestAccess
            : (virtualCard.recurs &&
                  virtualCard.pending?.recurs === undefined) ||
                (!virtualCard.recurs && virtualCard.pending?.recurs)
              ? UpdateVirtualCardScreen.Recurring
              : UpdateVirtualCardScreen.Standard
      : UpdateVirtualCardScreen.Pick)
  const [screen, setScreen] = useState(currentScreen)

  useEffect((): void => {
    setScreen(currentScreen)
  }, [currentScreen])

  useEffect((): void => {
    if (virtualCard?.pending?.receiptAttachments !== undefined) {
      dispatch(
        createSetReceiptAttachmentIds(
          virtualCard?.pending?.receiptAttachments?.map((att) => att.id),
        ),
      )
    } else if (virtualCard?.receiptAttachments !== undefined) {
      dispatch(
        createSetReceiptAttachmentIds(
          virtualCard.receiptAttachments.map((att) => att.id),
        ),
      )
    }
  }, [screen, virtualCard])

  const negativeButton = virtualCard?.isPending
    ? {
        loading: canUpdateVirtualCard
          ? puttingVirtualCardReject
          : puttingVirtualCardCancel,
        text:
          canUpdateVirtualCard ||
          (isOrganizationCardApproverMe &&
            virtualCard.cardholderId !== user?.id)
            ? translate('Reject Card Request')
            : translate('Cancel Card Request'),
        onClick: (): void => {
          if (canUpdateVirtualCard || isOrganizationCardApproverMe) {
            dispatchPutVirtualCardRejectAndCloseModal(virtualCard.id)
            analytics?.track('manage vcn request', {
              type_of_card: virtualCard.cardTypeLabel.props.source,
              card_name: virtualCard.displayName,
              credit_limit: toDollars(virtualCard.balanceCents),
              active_until:
                virtualCard.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
              add_attachments:
                virtualCard.receiptAttachments !== undefined &&
                virtualCard.receiptAttachments.length > 0,
              add_notes: virtualCard.notes !== undefined,
              card_Created: false,
              request_status: 'Reject',
            })
          } else {
            dispatchPutVirtualCardCancelAndCloseModalOrRedirect(
              virtualCard.id,
              virtualCard.isPending,
            )
          }
        },
      }
    : virtualCard?.pending !== undefined
      ? {
          loading: canUpdateVirtualCard
            ? puttingVirtualCardReject
            : puttingVirtualCardCancelUpdate,
          text:
            canUpdateVirtualCard ||
            (isOrganizationCardApproverMe &&
              virtualCard.cardholderId !== user?.id)
              ? translate('Reject Update Request')
              : translate('Cancel Update Request'),
          onClick: (): void => {
            if (
              canUpdateVirtualCard ||
              (isOrganizationCardApproverMe &&
                virtualCard.cardholderId !== user?.id)
            ) {
              dispatchPutVirtualCardRejectAndCloseModal(virtualCard?.id)
            } else {
              dispatchPutVirtualCardCancelUpdateAndCloseModal(virtualCard.id)
            }
          },
        }
      : undefined

  // const hasCardsWithRequestedCurrency = issuingEnabledActiveRcnOptions.some((rcn) => rcn.details?.creditCard.currency === virtualCard?.currency)
  // const hasBudgetsWithRequestedCurrency = issuingEnabledActiveBudgetOptions.some((b) => b.details?.creditCard.currency === virtualCard?.currency)
  // const currencyError = virtualCard?.isPending && !hasCardsWithRequestedCurrency && !hasBudgetsWithRequestedCurrency && canUpdateVirtualCard
  const currencyError = false // TODO: seems low priority to me, but if we want this needs stat

  return (
    <>
      {loadingFlyout || !formStats.allLoaded ? (
        <LoadingBackgroundPage />
      ) : virtualCard !== undefined &&
        !virtualCard.isCancelledOrRejectedRequest &&
        virtualCardPermissions !== undefined &&
        (!virtualCardPermissions.canReadOnly || isOrganizationCardApproverMe) &&
        recipient !== undefined &&
        cardholder !== undefined &&
        !virtualCard.isClosed ? (
        <>
          <Helmet>
            <title>{`Update Virtual Card | ${appName}`}</title>
            <meta
              name="description"
              content={`Update Virtual Card page of ${appName}`}
            />
          </Helmet>
          {screen === UpdateVirtualCardScreen.Pick && (
            <UpdateVirtualCardPickContent
              id={virtualCardId}
              setScreen={setScreen}
              virtualCard={virtualCard}
              virtualCardPermissions={virtualCardPermissions}
              hideRecurring={!recurringVirtualCardsEnabled}
            />
          )}
          {screen === UpdateVirtualCardScreen.Standard && (
            <UpdateVirtualCardStandardContent
              cardholder={cardholder}
              currencyError={currencyError}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.standardCount === 0
              }
              labelCreditCardsAsAccounts={formStats.standardSourceCount > 0}
              creditCardOptions={formCreditCardStandardOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.standard.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.Recurring && (
            <UpdateVirtualCardRecurringContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              currencyError={currencyError}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.recurringCount === 0
              }
              labelCreditCardsAsAccounts={formStats.recurringSourceCount > 0}
              creditCardOptions={formCreditCardRecurringOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.recurring.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.RecurringGuest && (
            <UpdateVirtualCardRecurringContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              currencyError={currencyError}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.recurringCount === 0
              }
              labelCreditCardsAsAccounts={formStats.recurringSourceCount > 0}
              creditCardOptions={formCreditCardRecurringOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.recurring.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              isGuestCard={virtualCard?.direct}
              showSoftAlert={
                virtualCardPermissions.canUpdateVirtualCard &&
                cardApprovalsEnabled &&
                cardApprovalRules?.approverId !== user?.id
              }
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.GuestAccess && (
            <UpdateVirtualCardGuestAccessContent
              virtualCard={virtualCard}
              recipient={recipient}
              cardholder={cardholder}
              userOptions={[]}
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.guestAccessCount === 0
              }
              labelCreditCardsAsAccounts={formStats.guestAccessSourceCount > 0}
              creditCardOptions={formCreditCardGuestAccessOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.guestAccess.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={
                virtualCardPermissions.canUpdateVirtualCard &&
                cardApprovalsEnabled &&
                cardApprovalRules?.approverId !== user?.id
              }
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.BillPay && (
            <UpdateVirtualCardStandardContent
              cardholder={cardholder}
              currencyError={currencyError}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.billPayCount === 0
              }
              labelCreditCardsAsAccounts={formStats.billPaySourceCount > 0}
              creditCardOptions={formCreditCardBillPayOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.billPay.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
              isBillPay
            />
          )}
          {screen === UpdateVirtualCardScreen.IssuerCycle && (
            <UpdateVirtualCardIssuerCycleContent
              cardholder={cardholder}
              currencyError={currencyError}
              issuerCycleOptions={formCreditCards.issuerCycle.options}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formCreditCards.issuerCycle.options.length === 0
              }
              labelCreditCardsAsAccounts={true}
              creditCardOptions={formCreditCardIssuerCycleOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.issuerCycle.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
            />
          )}
          {screen === UpdateVirtualCardScreen.DecliningBalance && (
            <UpdateVirtualCardStandardContent
              cardholder={cardholder}
              currencyError={currencyError}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              negativeButton={negativeButton}
              puttingVirtualCard={puttingVirtualCard}
              putVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPutVirtualCardAndOpenToastAndCloseFlyout
              }
              receiptAttachments={receiptAttachments}
              recipient={recipient}
              setScreen={setScreen}
              topLevelError={topLevelError}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              userOptions={[]}
              virtualCard={virtualCard}
              isFormDisabled={
                virtualCard.isPending &&
                canUpdateVirtualCard &&
                formStats.balanceLockCount === 0
              }
              labelCreditCardsAsAccounts={formStats.balanceLockSourceCount > 0}
              creditCardOptions={formCreditCardBalanceLockOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.balanceLock.infiniteScrollProps
              }
              virtualCardPermissions={virtualCardPermissions}
              showSoftAlert={showPreApprovalSoftAlert}
              cardApproverId={cardApprovalRules?.approverId}
              typeofCard={VirtualCardTypes.decliningBalance}
            />
          )}
        </>
      ) : (
        <NotFoundRightFlyoutContent />
      )}
    </>
  )
}

export default UpdateVirtualCardForm
